import React, { useEffect, useState } from "react";

import SelectPartHeader from "./SelectPartHeader";
import Button from "../../../common/Button";
import Checkbox from "../../../common/Checkbox";

import { ButtonVariants } from "../../../common/Button/constants";
import { FakeLink, Input, InputLabel } from "../../../../constants/Styles";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/store";
import {
  BillableElement,
  BillOfMaterials,
  Board,
  SelectedElement,
} from "../../../../reducers/uploadedBoard";
import {
  useAddBoardElement,
  useBillOfMaterials,
  useDeleteBoardElement,
  useUpdateBoardElementSelection,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import * as styledElements from "./SelectPartStyles";
import { ConsignmentPartsHeader } from "./SelectPartStyles";
import Search from "../../../component-search/Search";
import { useUpdateSearch } from "../../../../utils/hooks/partSearch";
import { clearSearch } from "../../../../reducers/componentSearch";
import { partsAreTheSame } from "../../../../utils/parts";
import Categories from "../../../component-search/Categories";
import LoadingIcon from "../../../common/LoadingIcon";
import { useCategories } from "../../../../utils/hooks/commonly-stored/categories";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import _ from "lodash";
import { FieldTypes } from "../../../formik-form-components/FormikFormLine/constants";
import { OPTIONAL, REQUIRED } from "../../../../constants/Text";
import { useStringInput } from "../../../../utils/hooks/inputs";
import CustomParts from "../../../CustomParts";
import usePart from "../../../../utils/hooks/commonly-stored/parts";
import { SelectPartProperties } from "./ElementProperties";
import { DEMO_BOARD_SLUG } from "../../../../constants/boards";
import { InfoSource } from "../../../../reducers/models/parts";
import {
  elementsCouldBeUpdatedTogether,
  keyIsEmpty,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/billOfMaterials";

interface MatchingElement {
  element: BillableElement;
  elementId: string;
  selected: boolean;
}

export enum PartSelectionMode {
  PARTS = "Part Search",
  CUSTOM = "My Parts",
}

const MatchingCheckbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label?: string;
}) => (
  <Checkbox
    label={label}
    checked={checked}
    onChange={onChange}
    styleProps={{
      height: "24px",
      width: "24px",
    }}
    iconDimension={20}
  />
);

const getElement = (
  billOfMaterials: BillOfMaterials,
  selectedElement: SelectedElement,
): BillableElement | null => {
  if (selectedElement.elementId !== null) {
    return (
      billOfMaterials.elements.find(
        (element) => element.id == selectedElement.elementId,
      ) || null
    );
  }
  return null;
};

enum UpdateType {
  SelectPart,
  MarkDNP,
  RemovePart,
  DeleteElement,
}

const SelectPart = ({
  selectedElement,
  closeModal,
  groupElements,
}: {
  selectedElement: SelectedElement;
  closeModal: () => void;
  groupElements: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { loading, searchResponse, errored } = useAppSelector(
    (state) => state.componentSearch,
  );
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const updateBoardElementSelection = useUpdateBoardElementSelection();
  const addBoardElement = useAddBoardElement();
  const deleteBoardElement = useDeleteBoardElement();
  const updateSearch = useUpdateSearch();

  const board = useBoardFromContext().board as Board;
  const { categories } = useCategories();
  const element = getElement(billOfMaterials, selectedElement);
  const [selectedPartId, setSelectedPartId] = useState<string | null>(
    element?.part_id || null,
  );
  const { part: selectedPart } = usePart(selectedPartId);

  const [selectionMode, setSelectionMode] = useState<PartSelectionMode>(
    PartSelectionMode.PARTS,
  );

  const { value: nameField, bind: bindNameField } = useStringInput("");
  const { value: valueTextField, bind: bindValueTextField } =
    useStringInput("");

  const isDemoBoard = board.id == DEMO_BOARD_SLUG;

  useEffect(() => {
    if (
      searchResponse === null &&
      selectedPart !== null &&
      selectionMode === PartSelectionMode.PARTS
    ) {
      updateSearch({
        updateSearchParameters: { searchTerms: selectedPart.name, page: 1 },
      });
    }
  }, [searchResponse, selectedPart, updateSearch, selectionMode]);

  useEffect(() => {
    return () => {
      dispatch(clearSearch());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedPart !== null) {
      if (
        selectedPart.user_id ||
        selectedPart.source === InfoSource.MANUAL_ENTRY
      ) {
        setSelectionMode(PartSelectionMode.CUSTOM);
      } else {
        setSelectionMode(PartSelectionMode.PARTS);
      }
    }
  }, [selectedPart]);

  useEffect(() => {
    if (
      selectionMode == PartSelectionMode.PARTS &&
      searchResponse !== null &&
      !searchResponse.parts.some((part) =>
        partsAreTheSame([part.id, part.octopart_id], [selectedPartId]),
      )
    ) {
      setSelectedPartId(null);
    }
  }, [searchResponse, selectedPartId, selectionMode]);

  const selectedElementKeyIsEmpty =
    element !== null && keyIsEmpty(element.group_key);

  const [matchingElements, setMatchingElements] = useState<MatchingElement[]>(
    billOfMaterials.elements
      .filter((otherElement) => {
        if (element === null) {
          return false;
        } else if (otherElement.id == selectedElement.elementId) {
          return false;
        }
        return elementsCouldBeUpdatedTogether(element, otherElement);
      })
      .map((billableElement) => {
        return {
          element: billableElement,
          elementId: billableElement.id,
          selected:
            !selectedElementKeyIsEmpty &&
            groupElements &&
            billableElement.part_id == element?.part_id,
        };
      }),
  );

  if (categories === null) {
    return <LoadingIcon text={"Loading"} centerHorizontally centerVertically />;
  }

  const toggleAllMatchingElements = () => {
    const newMatchingElements = _.cloneDeep(matchingElements);
    const selectAll =
      matchingElements.filter((matchingElement) => {
        return !matchingElement.selected;
      }).length > 0;
    setMatchingElements(
      newMatchingElements.map((matchingElement) => {
        matchingElement.selected = selectAll;
        return matchingElement;
      }),
    );
  };

  const updatePartSelection = (updateType: UpdateType) => {
    if (updateType === UpdateType.DeleteElement) {
      if (selectedElement.elementId !== null && element !== null) {
        deleteBoardElement(
          [
            {
              elementName: element.name,
              elementId: selectedElement.elementId,
            },
          ].concat(
            matchingElements
              .filter((matchingElement) => matchingElement.selected)
              .map((matchingElement) => {
                return {
                  elementName: matchingElement.element.name,
                  elementId: matchingElement.elementId,
                };
              }),
          ),
          closeModal,
        );
      }
    }
    // I think Element ID shouldn't be null at this point. This just is the simplest way to satisfy the type checker
    if (selectedElement.elementId !== null && element !== null) {
      const determinePartUpdate = (
        currentPartId: string | null,
      ): string | null => {
        if (updateType === UpdateType.SelectPart) {
          return selectedPartId;
        } else if (updateType === UpdateType.RemovePart) {
          return null;
        }
        return currentPartId;
      };
      const determineDNP = (currentlyDNP: boolean) => {
        if (updateType === UpdateType.MarkDNP) {
          return true;
        } else {
          return currentlyDNP;
        }
      };
      updateBoardElementSelection(
        [
          {
            elementName: element.name,
            elementId: selectedElement.elementId,
            part_id: determinePartUpdate(element.part_id),
            isConsignment: element.is_consignment,
            lockAllocation: element.lock_allocation,
            doNotPopulate: determineDNP(element.do_not_populate),
            elementPlacement: null,
          },
        ].concat(
          matchingElements
            .filter((matchingElement) => matchingElement.selected)
            .map((matchingElement) => {
              return {
                elementName: matchingElement.element.name,
                elementId: matchingElement.elementId,
                part_id: determinePartUpdate(matchingElement.element.part_id),
                isConsignment: matchingElement.element.is_consignment,
                lockAllocation: element.lock_allocation,
                doNotPopulate: determineDNP(
                  matchingElement.element.do_not_populate,
                ),
                elementPlacement: null,
              };
            }),
        ),
        closeModal,
      );
    } else {
      void addBoardElement(
        [
          {
            name: nameField,
            valueText: valueTextField,
            doNotPopulate: false,
            part_id: selectedPartId,
            isConsignment: false,
            elementPlacement: null,
          },
        ],
        closeModal,
      );
    }
  };

  const selectMatchingElement = (matchingElement: MatchingElement) => {
    const newMatchingElements = (
      JSON.parse(JSON.stringify(matchingElements)) as MatchingElement[]
    ).map((newMatchingElement) => {
      if (newMatchingElement.elementId == matchingElement.elementId) {
        newMatchingElement.selected = !newMatchingElement.selected;
      }
      return newMatchingElement;
    });
    setMatchingElements(newMatchingElements);
  };

  const isUpdatePartDisabled =
    selectedPartId === null || loading || searchResponse?.parts?.length == 0;

  const isAddPartDisabled = loading || !nameField;

  const renderSearch = () => {
    if (searchResponse === null && !loading && !errored) {
      if (categories !== null) {
        return (
          <Categories
            categories={categories}
            description={
              "Browse the parts & components available for assembly on your board."
            }
            smallCards
            doNotRedirect
          />
        );
      } else {
        return <LoadingIcon text={"Loading"} />;
      }
    } else {
      return (
        <Search
          categories={categories}
          selectedPartId={selectedPartId}
          setSelectedPartId={setSelectedPartId}
          warnOnNoInventory
          noPartsComponent={
            <styledElements.NoPartsHelpfulTip>
              {"Don't see the part you're looking for? "}
              <FakeLink
                onClick={() => setSelectionMode(PartSelectionMode.CUSTOM)}
              >
                Add a part.
              </FakeLink>
            </styledElements.NoPartsHelpfulTip>
          }
        />
      );
    }
  };

  return (
    <styledElements.Container>
      <SelectPartHeader
        closeModal={closeModal}
        setSelectionMode={setSelectionMode}
        selectionMode={selectionMode}
      />
      <styledElements.Body>
        <styledElements.PartSearch>
          {selectionMode === PartSelectionMode.PARTS && renderSearch()}
          {selectionMode === PartSelectionMode.CUSTOM &&
            (isDemoBoard ? (
              <ConsignmentPartsHeader>
                Consignment parts are not available in the demo version.
              </ConsignmentPartsHeader>
            ) : (
              <CustomParts
                defaultUserId={board.user_id}
                setSelectedPartId={setSelectedPartId}
                selectedPart={selectedPart}
              />
            ))}
        </styledElements.PartSearch>
        <styledElements.RightSection>
          <styledElements.DetailsCard>
            {element === null ? (
              <styledElements.PropertiesContainer>
                <styledElements.Title>Add a part</styledElements.Title>
                <styledElements.PropertyContainer>
                  <InputLabel htmlFor="select-part-name">Name:</InputLabel>
                  <Input
                    id="select-part-name"
                    type={FieldTypes.text}
                    placeholder={REQUIRED}
                    name="Name"
                    {...bindNameField}
                  />
                </styledElements.PropertyContainer>
                <styledElements.PropertyContainer>
                  <InputLabel htmlFor="select-part-value-text">
                    Value text:
                  </InputLabel>
                  <Input
                    id="select-part-value-text"
                    type={FieldTypes.text}
                    placeholder={OPTIONAL}
                    name="Value text"
                    {...bindValueTextField}
                  />
                </styledElements.PropertyContainer>
              </styledElements.PropertiesContainer>
            ) : (
              <SelectPartProperties element={element} />
            )}
          </styledElements.DetailsCard>
          {matchingElements.length > 0 && (
            <styledElements.MatchingElements>
              Select which matching parts to update or remove from board:
              {matchingElements.map((matchingElement) => {
                const matchingElementName = matchingElement.element
                  .do_not_populate
                  ? `${matchingElement.element.name} (Marked do not populate)`
                  : matchingElement.element.name;

                return (
                  <styledElements.MatchingElement
                    key={matchingElement.elementId}
                  >
                    <MatchingCheckbox
                      checked={matchingElement.selected}
                      onChange={() => selectMatchingElement(matchingElement)}
                      label={matchingElementName}
                    />
                  </styledElements.MatchingElement>
                );
              })}
              {matchingElements.length > 2 && (
                <styledElements.MatchingElement>
                  <MatchingCheckbox
                    checked={matchingElements.every(
                      (element) => element.selected,
                    )}
                    onChange={toggleAllMatchingElements}
                    label={"Select All"}
                  />
                </styledElements.MatchingElement>
              )}
            </styledElements.MatchingElements>
          )}
          <styledElements.ButtonContainer>
            <Button
              label={element === null ? "Add part" : "Update part"}
              onClick={() => updatePartSelection(UpdateType.SelectPart)}
              disabled={
                element === null ? isAddPartDisabled : isUpdatePartDisabled
              }
              leftIconProps={{
                src: `/icons/update_part${
                  isUpdatePartDisabled ? "_black" : ""
                }.svg`,
                alt: "update-part",
                iconDimension: 24,
              }}
              styleProps={{ width: "100%" }}
              centerText
            />
            {element !== null && (
              <>
                <Button
                  label="Mark do not populate"
                  onClick={() => updatePartSelection(UpdateType.MarkDNP)}
                  variant={ButtonVariants.SECONDARY}
                  leftIconProps={{
                    src: "/icons/remove_gray.svg",
                    alt: "remove",
                    iconDimension: 24,
                  }}
                  styleProps={{ width: "100%" }}
                  centerText
                />
                <Button
                  label="Remove part selection"
                  onClick={() => updatePartSelection(UpdateType.RemovePart)}
                  variant={ButtonVariants.SECONDARY}
                  leftIconProps={{
                    src: "/icons/clear_gray.svg",
                    alt: "remove",
                    iconDimension: 24,
                  }}
                  styleProps={{ width: "100%" }}
                  centerText
                />
                <Button
                  label="Delete element"
                  onClick={() => updatePartSelection(UpdateType.DeleteElement)}
                  variant={ButtonVariants.SECONDARY}
                  leftIconProps={{
                    src: "/icons/trash_gray.svg",
                    alt: "delete",
                    iconDimension: 24,
                  }}
                  styleProps={{ width: "100%" }}
                  centerText
                />
              </>
            )}
          </styledElements.ButtonContainer>
        </styledElements.RightSection>
      </styledElements.Body>
    </styledElements.Container>
  );
};

export default SelectPart;
